// =============================================================================
// WebSocket connection — singleton with automatic reconnect, exponential
// backoff, and pub/sub event dispatch.
// =============================================================================

import { getToken } from './api';
import type { ChannelCategory } from './types';

export type WSEvent =
  | { type: 'hello'; userId: string; username: string; socketId: string; servers: string[]; onlineUserIds?: string[] }
  | { type: 'pong'; t: number }
  | { type: 'subscribed'; channelId: string }
  | { type: 'unsubscribed'; channelId: string }
  | { type: 'message.created'; channelId: string; message: import('./types').Message }
  | { type: 'message.updated'; channelId: string; message: import('./types').Message }
  | { type: 'message.deleted'; channelId: string; messageId: string }
  | { type: 'message.pinned'; channelId: string; messageId: string }
  | { type: 'message.unpinned'; channelId: string; messageId: string }
  | { type: 'typing.start'; channelId: string; userId: string; username: string; expiresAt: number }
  | { type: 'presence.update'; userId: string; username: string; status: 'online' | 'offline' }
  | { type: 'voice.participant_joined'; channelId: string; participant: { userId: string; username?: string; displayName?: string; avatarUrl?: string | null } }
  | { type: 'voice.participant_left'; channelId: string; participant: { userId: string; username?: string } }
  | { type: 'voice.share_started'; channelId: string; userId: string }
  | { type: 'voice.share_stopped'; channelId: string; userId: string }
  | { type: 'voice.server_mute'; serverId: string; userId: string; muted: boolean }
  | { type: 'voice.room_ended'; channelId: string }
  | { type: 'member.joined'; serverId: string; member: import('./types').Member }
  | { type: 'member.left'; serverId: string; userId: string }
  | { type: 'member.role_changed'; serverId: string; userId: string; role: 'owner' | 'admin' | 'moderator' | 'member' }
  | { type: 'channel.deleted'; serverId: string; channelId: string }
  | { type: 'channel.reordered'; serverId: string; channelType: 'text' | 'voice'; channelIds: string[] }
  | { type: 'channel.moved'; serverId: string; categoryId: string | null; channelIds: string[] }
  | { type: 'category.created'; serverId: string; category: ChannelCategory }
  | { type: 'category.updated'; serverId: string; category: ChannelCategory }
  | { type: 'category.deleted'; serverId: string; categoryId: string }
  | { type: 'category.reordered'; serverId: string; categoryIds: string[] }
  | { type: 'emoji.created'; serverId: string; emoji: { id: string; serverId: string; name: string; url: string; fullUrl: string; createdAt: string } }
  | { type: 'emoji.deleted'; serverId: string; emojiId: string }
  | { type: 'server.updated'; serverId: string; iconUrl?: string | null; name?: string }
  | { type: 'channel.read'; userId: string; channelId: string; readAt: string }
  | { type: 'reaction.added'; channelId: string; messageId: string; userId: string; emoji: string }
  | { type: 'reaction.removed'; channelId: string; messageId: string; userId: string; emoji: string }
  | { type: 'message.embed'; channelId: string; messageId: string; embeds: Array<{ url: string; title: string | null; description: string | null; imageUrl: string | null; siteName: string | null }> }
  | { type: 'user.updated'; user: { id: string; username: string; displayName: string | null; avatarUrl: string | null } }
  | { type: 'message.ack'; nonce?: string; message: import('./types').Message }
  // DM call signaling (mirror of the gateway's outbound call.* events).
  | { type: 'call.incoming'; toUserId: string; dmChannelId: string; from: { id: string; username: string; displayName: string | null; avatarUrl: string | null } | null }
  | { type: 'call.ringing'; toUserId: string; dmChannelId: string }
  | { type: 'call.accepted'; toUserId: string; dmChannelId: string }
  | { type: 'call.declined'; toUserId: string; dmChannelId: string }
  | { type: 'call.cancelled'; toUserId: string; dmChannelId: string; reason?: string }
  | { type: 'call.ended'; toUserId: string; dmChannelId: string; reason?: string; durationSec?: number | null }
  | { type: 'call.busy'; dmChannelId: string; reason?: string }
  | { type: 'call.error'; dmChannelId: string; error: string }
  // Friend graph changes — the recipient refetches its friends list.
  | { type: 'friend.request'; toUserId: string; fromUserId: string }
  | { type: 'friend.accepted'; toUserId: string; fromUserId: string }
  | { type: 'friend.removed'; toUserId: string; fromUserId: string }
  // In-app notifications (wall posts, etc.) targeted at the recipient.
  | { type: 'notification.created'; toUserId: string; notification: import('./types').Notification }
  | { type: 'notification.read'; toUserId: string; id: string | null }
  | { type: 'error'; error: string };

export type ConnectionState = 'disconnected' | 'connecting' | 'connected';

type Listener = (event: WSEvent) => void;
type StateListener = (state: ConnectionState) => void;

class WebSocketClient {
  private ws: WebSocket | null = null;
  private state: ConnectionState = 'disconnected';
  private listeners = new Set<Listener>();
  private stateListeners = new Set<StateListener>();
  private reconnectAttempts = 0;
  private reconnectTimer: number | null = null;
  private intentionallyClosed = false;
  /** Set by App.tsx: runs the sign-out when the server ends our session. */
  onAuthLost: (() => void) | null = null;
  private subscriptions = new Set<string>();  // re-subscribe on reconnect

  connect(): void {
    if (this.ws && this.ws.readyState !== WebSocket.CLOSED) return;
    const token = getToken();
    if (!token) return;

    this.intentionallyClosed = false;
    this.setState('connecting');

    // In dev: same-origin /ws gets proxied. In prod: same-origin /ws is the backend.
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = `${proto}//${window.location.host}/ws?token=${encodeURIComponent(token)}`;

    try {
      this.ws = new WebSocket(url);
    } catch (err) {
      console.error('WS create failed', err);
      this.scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      this.reconnectAttempts = 0;
      this.setState('connected');
      // Re-subscribe to channels
      for (const channelId of this.subscriptions) {
        this.send({ type: 'subscribe', channelId });
      }
    };

    this.ws.onmessage = (e) => {
      let event: WSEvent;
      try {
        event = JSON.parse(e.data);
      } catch {
        return;
      }
      for (const listener of this.listeners) {
        try {
          listener(event);
        } catch (err) {
          console.error('WS listener error', err);
        }
      }
    };

    this.ws.onerror = (e) => {
      console.warn('WS error', e);
    };

    this.ws.onclose = (e) => {
      this.ws = null;
      this.setState('disconnected');
      // 4001: this session was signed out from another device. 1008: the
      // token was rejected at connect (revoked/expired). Reconnecting can't
      // help either way — it just loops — so hand off to the sign-out path.
      if (e.code === 4001 || e.code === 1008) {
        this.intentionallyClosed = true;
        this.onAuthLost?.();
        return;
      }
      if (!this.intentionallyClosed) this.scheduleReconnect();
    };
  }

  disconnect(): void {
    this.intentionallyClosed = true;
    if (this.reconnectTimer) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.subscriptions.clear();
    this.ws?.close();
  }

  private scheduleReconnect(): void {
    if (this.intentionallyClosed) return;
    this.reconnectAttempts++;
    // Exponential backoff: 1s, 2s, 4s, ..., max 30s
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 30_000);
    this.reconnectTimer = window.setTimeout(() => this.connect(), delay);
  }

  private setState(state: ConnectionState): void {
    this.state = state;
    for (const l of this.stateListeners) l(state);
  }

  send(message: object): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  subscribeToChannel(channelId: string): void {
    this.subscriptions.add(channelId);
    this.send({ type: 'subscribe', channelId });
  }

  unsubscribeFromChannel(channelId: string): void {
    this.subscriptions.delete(channelId);
    this.send({ type: 'unsubscribe', channelId });
  }

  on(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onState(listener: StateListener): () => void {
    this.stateListeners.add(listener);
    listener(this.state);  // call immediately with current state
    return () => this.stateListeners.delete(listener);
  }

  getState(): ConnectionState {
    return this.state;
  }
}

export const wsClient = new WebSocketClient();
